import { useTheme as useWeaveTheme } from '@mui/material';

import {
  combineWeaveTheme,
  isCombinedTheme,
  type CombinedTheme,
  type MaybeCombinedTheme,
} from '../combineWeaveTheme';
import { WEAVE_THEME_ID } from '../constants';
import { type WeaveTheme } from '../types/weaveTypes';

export type { CombinedTheme } from '../combineWeaveTheme';

/**
 * Caches the combined theme per Weave theme reference (stable across renders,
 * changes only on theme swap), collapsing the per-component/per-render
 * `getAccWeaveTheme` + MUI deepmerge to once per theme (DSGN-2450).
 *
 * When the theme is provided through `WeaveThemeGate`, the context already
 * holds the combined theme, so `isCombinedTheme` short-circuits before this
 * cache is ever consulted (DSGN-2615). The cache remains as a fallback for
 * consumers that provide a raw Weave theme directly, without the gate.
 */
const combinedThemeCache = new WeakMap<WeaveTheme, CombinedTheme>();

export const useTheme = (): CombinedTheme => {
  const maybeAlloyTheme = useWeaveTheme<MaybeCombinedTheme>();

  if (isCombinedTheme(maybeAlloyTheme)) {
    return maybeAlloyTheme;
  }

  const weaveTheme = maybeAlloyTheme;
  const cached = combinedThemeCache.get(weaveTheme);
  if (cached) {
    return cached;
  }

  const combined = combineWeaveTheme(weaveTheme);
  combinedThemeCache.set(weaveTheme, combined);
  return combined;
};

export const isWeaveTheme = (theme: CombinedTheme) =>
  theme.id === WEAVE_THEME_ID;
